import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Pause, Play, SkipForward, Square, RotateCcw, Check } from 'lucide-react';
import { CircularProgress } from '@/components/CircularProgress';
import { TimerDisplay } from '@/components/TimerDisplay';
import { IntervalIndicator } from '@/components/IntervalIndicator';
import { useTimer } from '@/hooks/useTimer';
import { useApp } from '@/context/AppContext';
import { findRoutineById } from '@/data/presets';
import { generatePhases, formatTime, calcTotalDuration } from '@/models/models';
import { phaseColor, phaseGradient } from '@/theme/colors';
import { audioService } from '@/services/audio-service';

const PHASE_LABEL = {
  work: 'WORK',
  rest: 'REST',
  restBlock: 'BLOCK REST',
  countdown: 'GET READY',
  cooldown: 'COOLDOWN',
};

export const ActiveWorkout = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { routines, settings } = useApp();

  const routine = useMemo(
    () => findRoutineById(id, routines),
    [id, routines]
  );

  const phases = useMemo(
    () =>
      routine
        ? generatePhases(routine, {
            countdownBefore: settings.countdownBefore,
            countdownSeconds: settings.countdownSeconds,
          })
        : [],
    [routine, settings.countdownBefore, settings.countdownSeconds]
  );

  const lastTickRef = useRef(-1);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    setReduceMotion(
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }, []);

  const handlePhaseChange = (next, prev) => {
    lastTickRef.current = -1;
    if (prev && prev.type === 'work') audioService.workEnd();
    if (prev && prev.isRoundEnd) audioService.roundComplete();
    if (!next) return;
    if (next.type === 'work') audioService.workStart();
    else if (next.type === 'rest' || next.type === 'restBlock')
      audioService.restStart();
  };

  const handleFinish = () => {
    audioService.sessionComplete();
  };

  const {
    phase,
    remaining,
    remainingSec,
    progress,
    status,
    isRunning,
    isPaused,
    isFinished,
    controls,
  } = useTimer(phases, {
    autoStart: settings.autoStart,
    onPhaseChange: handlePhaseChange,
    onFinish: handleFinish,
    onTick: (rem) => {
      const sec = Math.ceil(rem - 0.0001);
      if (sec <= 3 && sec >= 1 && sec !== lastTickRef.current) {
        lastTickRef.current = sec;
        audioService.countdownTick();
      }
    },
  });

  // keep screen awake (best effort, simulated)
  useEffect(() => {
    let lock = null;
    if (settings.keepAwake && 'wakeLock' in navigator) {
      navigator.wakeLock.request('screen').then((l) => (lock = l)).catch(() => {});
    }
    return () => {
      if (lock) lock.release().catch(() => {});
    };
  }, [settings.keepAwake]);

  if (!routine) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#0F172A] text-white">
        <p>Routine not found</p>
        <button
          onClick={() => navigate('/home')}
          className="rounded-xl bg-[#38BDF8] px-6 py-3 font-bold text-[#0F172A]"
        >
          Back Home
        </button>
      </div>
    );
  }

  const type = phase ? phase.type : 'countdown';
  const color = phaseColor(type);
  const isCountdown = type === 'countdown';
  const pulse = !reduceMotion && (isCountdown || (remainingSec <= 3 && remainingSec >= 1));

  const totalDuration = calcTotalDuration(routine, true);

  const stopWorkout = () => {
    controls.stop();
    navigate('/home');
  };

  return (
    <motion.div
      className="relative flex min-h-screen flex-col overflow-hidden"
      animate={{ background: phaseGradient(type) }}
      transition={{ duration: reduceMotion ? 0 : 0.4 }}
      style={{ background: phaseGradient(type) }}
      data-testid="active-workout"
      data-phase={type}
    >
      <div className="ns-bg-grain" />

      {/* header */}
      <div className="relative z-10 flex items-center justify-between px-6 pt-8">
        <span className="text-sm font-bold uppercase tracking-[0.3em] text-white/80">
          {routine.name}
        </span>
        <span className="text-sm font-semibold text-white/70 tabular-nums">
          {formatTime(totalDuration)}
        </span>
      </div>

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center gap-6 px-6">
        {!isFinished && phase && (
          <>
            <motion.p
              key={`ph-${type}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-lg font-extrabold uppercase tracking-[0.35em] text-white/90"
              data-testid="phase-label"
            >
              {PHASE_LABEL[type] || type}
            </motion.p>

            {phase.totalRounds > 0 && (
              <p className="text-base font-bold tracking-widest text-white/70 tabular-nums">
                ROUND {phase.round} / {phase.totalRounds}
              </p>
            )}

            <CircularProgress
              progress={progress}
              size={300}
              color="#FFFFFF"
              trackColor="rgba(255,255,255,0.18)"
            >
              <TimerDisplay
                seconds={remaining}
                pulse={pulse}
                color="#FFFFFF"
              />
            </CircularProgress>

            <AnimatePresence mode="wait">
              <motion.h2
                key={phase.label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.3 }}
                className="text-3xl font-extrabold text-white"
                data-testid="exercise-label"
              >
                {phase.label}
              </motion.h2>
            </AnimatePresence>

            {phase.next && (
              <p className="text-sm font-medium text-white/70">
                Next: {phase.next}
              </p>
            )}

            {phase.totalRounds > 0 && (
              <IntervalIndicator
                total={phase.totalRounds}
                current={phase.round}
                color="#FFFFFF"
              />
            )}
          </>
        )}

        {isFinished && (
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            className="flex flex-col items-center gap-6 text-center"
            data-testid="workout-complete"
          >
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white/15">
              <Check size={56} className="text-white" strokeWidth={3} />
            </div>
            <div>
              <h2 className="text-4xl font-extrabold text-white">Complete!</h2>
              <p className="mt-2 text-white/80">Great work. Session finished.</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => controls.restart()}
                data-testid="restart-btn"
                className="flex min-h-[56px] items-center gap-2 rounded-2xl bg-white/15 px-6 font-bold text-white"
              >
                <RotateCcw size={20} /> Restart
              </button>
              <button
                onClick={() => navigate('/home')}
                data-testid="finish-home-btn"
                className="flex min-h-[56px] items-center gap-2 rounded-2xl bg-white px-6 font-bold text-[#0F172A]"
              >
                Done
              </button>
            </div>
          </motion.div>
        )}
      </div>

      {/* controls */}
      {!isFinished && (
        <div className="relative z-10 flex items-center justify-center gap-3 px-6 pb-10">
          {status === 'idle' ? (
            <button
              onClick={() => controls.start()}
              data-testid="play-btn"
              className="flex min-h-[64px] flex-1 items-center justify-center gap-2 rounded-2xl bg-white text-lg font-extrabold text-[#0F172A]"
            >
              <Play size={22} fill="currentColor" /> START
            </button>
          ) : (
            <>
              <button
                onClick={() => (isPaused ? controls.resume() : controls.pause())}
                data-testid="pause-btn"
                className="flex min-h-[64px] flex-1 items-center justify-center gap-2 rounded-2xl bg-white/20 text-lg font-extrabold text-white backdrop-blur"
              >
                {isPaused ? (
                  <>
                    <Play size={22} fill="currentColor" /> RESUME
                  </>
                ) : (
                  <>
                    <Pause size={22} fill="currentColor" /> PAUSE
                  </>
                )}
              </button>
              <button
                onClick={() => controls.skip()}
                data-testid="skip-btn"
                aria-label="Skip"
                className="flex h-[64px] w-[64px] items-center justify-center rounded-2xl bg-white/15 text-white"
              >
                <SkipForward size={24} />
              </button>
              <button
                onClick={stopWorkout}
                data-testid="stop-btn"
                aria-label="Stop"
                className="flex h-[64px] w-[64px] items-center justify-center rounded-2xl bg-white/15 text-[#FCA5A5]"
              >
                <Square size={22} fill="currentColor" />
              </button>
            </>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default ActiveWorkout;
